<div class="piglet-manage-article-list">
  <h1 class="text-2xl font-bold mb-4">{{ 'PIGLET.ARTICLE_LIST.TITLE' | translate }}</h1>
  <button
    (click)="createNewArticle()"
    class="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center"
  >
    <i class="uil uil-plus mr-2"></i> {{ 'PIGLET.ARTICLE_LIST.CREATE_NEW' | translate }}
  </button>

  <!-- Error message -->
  <div *ngIf="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
    {{ error }}
  </div>

  <!-- Loading indicator for initial load -->
  <div *ngIf="loading" class="flex justify-center my-8">
    <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-600"></div>
  </div>

  <!-- Hierarchical display of category types, categories, and articles -->
  <div *ngIf="!loading" class="space-y-8">
    <!-- Iterate through category types -->
    <div *ngFor="let categoryType of categoryTypesWithCategories" class="category-type-section">
      <!-- Category Type Header (h2) -->
      <div class="flex items-center mb-4">
        <h2 class="text-xl font-bold">{{ categoryType.categoryType }}</h2>
        <button
          (click)="toggleCategoryTypeExpansion(categoryType.categoryId)"
          class="ml-2"
        >
          {{ categoryType.expanded ? '▼' : '►' }}
        </button>
      </div>

      <!-- Categories within this type -->
      <div *ngIf="categoryType.expanded" class="space-y-6 pl-4">
        <!-- Iterate through categories -->
        <div *ngFor="let category of categoryType.categories" class="category-section">
          <!-- Category Header (h3) -->
          <h3 class="text-lg font-semibold mb-3">{{ category.category }}</h3>

          <!-- Loading indicator for category articles -->
          <div *ngIf="category.paginationState.loading" class="flex justify-center my-4">
            <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-violet-600"></div>
          </div>

          <!-- Articles Table for this category -->
          <div *ngIf="!category.paginationState.loading && (category.articles?.length ?? 0) > 0" class="overflow-x-auto">
            <table class="table-auto w-full border-collapse border border-slate-400">
              <thead>
                <tr class="bg-slate-100 dark:bg-slate-700">
                  <th class="border border-slate-300 dark:border-slate-600 p-2">{{ 'PIGLET.ARTICLE_LIST.ID' | translate }}</th>
                  <th class="border border-slate-300 dark:border-slate-600 p-2">{{ 'PIGLET.ARTICLE_LIST.TITLE' | translate }}</th>
                  <th class="border border-slate-300 dark:border-slate-600 p-2">{{ 'PIGLET.ARTICLE_LIST.LANGUAGES' | translate }}</th>
                  <th class="border border-slate-300 dark:border-slate-600 p-2">{{ 'PIGLET.ARTICLE_LIST.AUTHOR' | translate }}</th>
                  <th class="border border-slate-300 dark:border-slate-600 p-2">{{ 'PIGLET.ARTICLE_LIST.PUBLISHED' | translate }}</th>
                  <th class="border border-slate-300 dark:border-slate-600 p-2">{{ 'PIGLET.ARTICLE_LIST.CREATED' | translate }}</th>
                  <th class="border border-slate-300 dark:border-slate-600 p-2">{{ 'PIGLET.ARTICLE_LIST.UPDATED' | translate }}</th>
                  <th class="border border-slate-300 dark:border-slate-600 p-2">{{ 'PIGLET.ARTICLE_LIST.ACTIONS' | translate }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let article of category.articles; let even = even" [ngClass]="even ? 'bg-slate-50 dark:bg-slate-800' : 'bg-white dark:bg-slate-900'">
                  <td class="border border-slate-300 dark:border-slate-600 p-2">{{ article.id }}</td>
                  <td class="border border-slate-300 dark:border-slate-600 p-2">{{ article.title }}</td>
<!--                  <td class="border border-slate-300 dark:border-slate-600 p-2" [innerHTML]="formatLanguages(article.availableLanguages, article.id)"></td>-->
                  <td class="border border-slate-300 dark:border-slate-600 p-2">
                    <p *ngFor="let lang of article.availableLanguages">
                      <a class="text-indigo-600 hover:text-indigo-900 cursor-pointer" (click)="editArticleWithLanguage(article.id, lang.toLowerCase())">{{ lang }}</a>
                    </p>
                  </td>
                  <td class="border border-slate-300 dark:border-slate-600 p-2">{{ article.author }}</td>
                  <td class="border border-slate-300 dark:border-slate-600 p-2">
                    <i *ngIf="article.isPublished" class="mdi mdi-check-bold text-green-600"
                       [title]="'PIGLET.ARTICLE_LIST.ARTICLE_PUBLISHED' | translate"></i>
                    <i *ngIf="!article.isPublished" class="mdi mdi-close-thick text-red-600"
                       [title]="'PIGLET.ARTICLE_LIST.ARTICLE_NOT_PUBLISHED' | translate"></i>
                    <i *ngIf="article.isHidden" class="mdi mdi-file-hidden text-orange-600"
                       [title]="'PIGLET.ARTICLE_LIST.ARTICLE_HIDDEN' | translate"></i>
                  </td>
                  <td class="border border-slate-300 dark:border-slate-600 p-2">{{ formatDate(article.createTime) }}</td>
                  <td class="border border-slate-300 dark:border-slate-600 p-2">{{ formatDate(article.updateTime) }}</td>
                  <td class="border border-slate-300 dark:border-slate-600 p-2">
                    <div class="flex space-x-2">
                      <button
                        (click)="editArticle(article.id)"
                        class="text-indigo-600 hover:text-indigo-900"
                      >
                        <i class="uil uil-edit h-28 w-28 fill-violet-600/5 mx-auto rotate-[30deg]"></i>
                      </button>
                      <button
                        (click)="toggleArticleVisibility(categoryType.categoryId, category, article)"
                        [ngClass]="(article.isHidden ? ['text-green-600', 'hover:text-green-900'] : ['text-orange-600', 'hover:text-orange-900'])"
                        [title]="(article.isHidden ? ('PIGLET.ARTICLE_LIST.MAKE_VISIBLE' | translate) : ('PIGLET.ARTICLE_LIST.REMOVE_VISIBILITY' | translate))"
                      >
                        <i class="uil uil-eye h-28 w-28 fill-violet-600/5 mx-auto"></i>
                      </button>
                      <button
                        (click)="togglePublishStatus(categoryType.categoryId, category, article)"
                        [ngClass]="(!article.isPublished ? ['text-green-600', 'hover:text-green-900'] : ['text-red-600', 'hover:text-red-900'])"
                        [title]="(!article.isPublished ? ('PIGLET.ARTICLE_LIST.PUBLISH' | translate) : ('PIGLET.ARTICLE_LIST.UNPUBLISH' | translate))"
                      >
                        <i class="uil uil-cloud-redo h-28 w-28 fill-violet-600/5 mx-auto"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Pagination for this category -->
            <div class="flex justify-between items-center mt-4">
              <div class="text-sm text-gray-300">
                {{ 'PIGLET.ARTICLE_LIST.SHOWING' | translate }} <span class="font-medium">{{ category.articles.length }}</span> {{ 'PIGLET.ARTICLE_LIST.OF' | translate }}
                <span class="font-medium">{{ category.paginationState.totalItems }}</span> {{ 'PIGLET.ARTICLE_LIST.ARTICLES' | translate }}
              </div>
              <div class="flex space-x-2">
                <button
                  (click)="onPageChange(categoryType.categoryId, category.categoryId, category.paginationState.currentPage - 1)"
                  [disabled]="category.paginationState.currentPage === 0"
                  class="px-3 py-1 border rounded-md text-sm font-medium"
                  [ngClass]="category.paginationState.currentPage === 0 ? 'text-gray-400 border-gray-300 cursor-not-allowed' : 'text-indigo-600 border-indigo-500 hover:bg-indigo-50'"
                >
                  {{ 'PIGLET.ARTICLE_LIST.PREVIOUS' | translate }}
                </button>
                <button
                  (click)="onPageChange(categoryType.categoryId, category.categoryId, category.paginationState.currentPage + 1)"
                  [disabled]="(category.paginationState.currentPage + 1) * category.paginationState.itemsPerPage >= category.paginationState.totalItems"
                  class="px-3 py-1 border rounded-md text-sm font-medium"
                  [ngClass]="(category.paginationState.currentPage + 1) * category.paginationState.itemsPerPage >= category.paginationState.totalItems ? 'text-gray-400 border-gray-300 cursor-not-allowed' : 'text-indigo-600 border-indigo-500 hover:bg-indigo-50'"
                >
                  {{ 'PIGLET.ARTICLE_LIST.NEXT' | translate }}
                </button>
              </div>
            </div>
          </div>

          <!-- No articles message for this category -->
          <div *ngIf="!category.paginationState.loading && category.articlesLoaded && (!category.articles || category.articles.length === 0)" class="text-center py-4">
            <p class="text-gray-300">{{ 'PIGLET.ARTICLE_LIST.NO_ARTICLES' | translate }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- No category types message -->
  <div *ngIf="!loading && categoryTypesWithCategories.length === 0" class="text-center py-8">
    <p class="text-gray-300">{{ 'PIGLET.ARTICLE_LIST.NO_CATEGORY_TYPES' | translate }}</p>
  </div>
</div>
